import { AppState } from 'pages';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  boardState,
  BoardStatus,
  setLenderDelete,
  setStatus
} from 'common/modules/app/board';
import { AddLendingUserBoard } from '../components/AddLendingUserBoard';
import { loadCookie, SasCookieKeys } from 'common/utils/cookie-utils';
import {
  postLendingUserList,
  POST_LENDING_USER_ID
} from 'common/modules/domain/lendingUser';
import { endFetch, FetchState, FetchStatus } from 'common/modules/app/fetch';
import { ErrorState } from 'common/modules/app/errors';
import { Glossary } from 'common/utils/glossary';
import { GlossaryState } from 'common/modules/app/glossaries';
import { UserInformation } from 'common/modules/domain/user';
import { sasStaticUrl } from 'common/utils/urls';

const _isInvalidAddress = (errorState: ErrorState) => {
  return (
    errorState.requestId === POST_LENDING_USER_ID &&
    errorState.statusCode === 400 &&
    errorState.message === 'S23402'
  );
};

/**
 * StoreのStateからComponentに渡すPropsへ
 */
const useStateProps = () => {
  const boardState = useSelector<AppState, boardState>(
    (state) => state.app.board
  );
  const fetchState = useSelector<AppState, FetchState>(
    (state) => state.app.fetch
  );
  const errorState = useSelector<AppState, ErrorState>(
    (state) => state.app.error
  );
  const glState = useSelector<AppState, GlossaryState>(
    (state) => state.app.glossaries
  );
  const userState = useSelector<AppState, UserInformation>(
    (state) => state.domain.user
  );
  const isLendingUserProcessing =
    fetchState[POST_LENDING_USER_ID] !== null &&
    fetchState[POST_LENDING_USER_ID] === FetchStatus.PROCESSING;
  const isLendingUserProcessingFail =
    fetchState[POST_LENDING_USER_ID] !== null &&
    fetchState[POST_LENDING_USER_ID] === FetchStatus.FAILED;
  return {
    purchaseId: boardState.selectedPurchaseIdForLendingUser,
    isDisplay: boardState.status === BoardStatus.ADD_LENDING_USER_DISP,
    isInvalidAddress: _isInvalidAddress(errorState),
    isLendingUserProcessing,
    isLendingUserProcessingFail,
    glossary: Glossary.instance(glState),
    user: userState
  };
};

type SelectedState = ReturnType<typeof useStateProps>;

/**
 * Actionをdispatchする関数からComponentに渡すPropsへ
 */
const useMargeProps = (state: SelectedState) => {
  const dispatch = useDispatch();
  const cookie = loadCookie();
  const _token = cookie[SasCookieKeys.KEY_TOKEN];
  const _refreshToken = cookie[SasCookieKeys.KEY_REFRESH_TOKEN];
  const _purchaseId = state.purchaseId;
  const _language = state.user.language;

  /** 初期化処理 */
  const onInit = () => {
    dispatch(endFetch(POST_LENDING_USER_ID));
  };

  /** ×ボタンの押下処理 */
  const onClickClose = () => {
    if (!state.isLendingUserProcessing) {
      dispatch(setStatus(BoardStatus.LENDING_USER_DISP));
    }
  };

  /** 登録ボタンの押下処理 */
  const onClickRegister = (email: string) => {
    dispatch(setLenderDelete(true));
    dispatch(
      postLendingUserList(_token, _refreshToken, _purchaseId, email, _language)
    );
  };

  /** "詳しい条件を確認する"クリック時の処理 */
  const onClickTermsLink = () => {
    // 「詳しい条件を確認する」を別タブで開く
    window.open(`${sasStaticUrl}/st/material-conditions.html`);
  };

  return {
    isDisplay: state.isDisplay,
    isLendingUserProcessing: state.isLendingUserProcessing,
    isLendingUserProcessingFail: state.isLendingUserProcessingFail,
    isInvalidAddress: state.isInvalidAddress,
    glossary: state.glossary,
    onInit,
    onClickClose,
    onClickRegister,
    onClickTermsLink
  };
};

export default function ContainerComponent() {
  const _props = { ...useMargeProps(useStateProps()) };
  return <AddLendingUserBoard {..._props} />;
}
